import createAuth0Client, { Auth0Client, Auth0ClientOptions } from '@auth0/auth0-spa-js';
import { History } from 'history';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { dispatch } from 'src/redux/config/configureStore';
import { setRedirectUrl } from 'src/redux/slices/investor.slice';
import Auth0Context, { ContextProps } from './authContext.context';

interface Auth0ProviderProps {
  children: ReactNode;
  history: History;
}

interface Auth0State {
  auth0Client: Auth0Client | null;
  loading: boolean;
  isAuthenticated: boolean;
  user: any;
  popupOpen: boolean;
}

const Auth0Provider: React.FC<Auth0ProviderProps> = ({ children, history }) => {
  const [state, setState] = useState<Auth0State>({
    auth0Client: null,
    loading: true,
    isAuthenticated: false,
    user: null,
    popupOpen: false,
  });

  // Get the current subdomain dynamically
  const subdomain =
    window.location.hostname.split('.')?.length > 1 ? window.location.hostname.split('.')[0] : '';
  let redirectUri = process.env.REACT_APP_AUTH0_REDIRECT_URL;

  // Construct the redirect_uri based on the subdomain
  if (subdomain && subdomain?.trim() !== '' && subdomain.trim() !== 'app') {
    redirectUri = redirectUri.replace('https://' || 'http://', ''); // remove the https:// or http://
    redirectUri = `${subdomain}.${redirectUri}`; // combine the subdomain with the redirect_uri
    //add the https:// to the subdomain
    redirectUri = `https://${redirectUri}`;
  }

  const config: Auth0ClientOptions = useMemo(
    () => ({
      domain: process.env.REACT_APP_AUTH0_DOMAIN!,
      client_id: process.env.REACT_APP_AUTH0_CLIENT_ID!,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      redirect_uri: redirectUri,
    }),
    [redirectUri],
  );

  useEffect(() => {
    initializeAuth0();
  }, []);

  const initializeAuth0 = async () => {
    const auth0Client = await createAuth0Client(config);
    if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
      try {
        const { appState } = await auth0Client.handleRedirectCallback();
        onRedirectCallback(appState);
      } catch (err) {
        console.error('auth0 error: ', err);
      }
    }
    const isAuthenticated = await auth0Client.isAuthenticated();
    const user = isAuthenticated ? await auth0Client.getUser() : null;

    setTimeout(() => {
      const userWithMetaData = arrangeMetaToUser(user);
      setState(prevState => ({
        ...prevState,
        auth0Client,
        loading: false,
        isAuthenticated,
        user: userWithMetaData,
      }));
    }, 700);
  };

  /**
   * This function is used to reload the page if the device is mobile and the width is a desktop width
   * ! A fix for the auth0 redirect resolution issue - when auth0 redirects after login, the viewport for mobile devices is not set correctly
   */
  const reloadWindowAuth0ResFix = () => {
    // check if the device is mobile
    const width = window.innerWidth;
    const pixelRatio = window.devicePixelRatio || 1;
    const platform = navigator.platform || '';

    const isTouchCapable = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobilePlatform = /iPhone|iPad|iPod|Android|Linux arm/i.test(platform);
    const isHighDPR = pixelRatio > 1.3;
    const isPortrait = window.innerHeight > window.innerWidth;

    const isMobile = isTouchCapable && isMobilePlatform && isHighDPR && isPortrait;

    const url = new URL(window.location.href);
    const alreadyFixed = url.searchParams.get('viewportFixed');
    const viewAsParam = url.searchParams.get('view_as_email');

    // ✅ Check if the device is mobile and the width is a desktop width
    if (isMobile && width === 980 && !alreadyFixed) {
      // ✅ Set flag in the URL to prevent repeat
      url.searchParams.set('viewportFixed', 'true');
      if (viewAsParam) {
        url.searchParams.set('view_as_email', viewAsParam);
      }
      window.history.replaceState({}, '', url.toString());

      // ✅ Now safe to reload once
      window.location.reload();
    }
  };

  const onRedirectCallback = (appState: any | undefined) => {
    // Set the redirect URL
    dispatch(setRedirectUrl(appState?.returnTo || window.location.pathname));

    // Apply auth0 redirect resolution fix
    reloadWindowAuth0ResFix();

    // Scroll to the top of the window after navigating
    window.scrollTo(0, 0);
  };

  const arrangeMetaToUser = (user: any) => {
    if (user) {
      const meta_data = user['https://example.com/metaData'];
      return { ...user, meta_data };
    }
    return user;
  };

  const loginWithPopup = async (params = {}) => {
    let popupOpen = true;
    try {
      await state.auth0Client!.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      popupOpen = false;
    }
    const user = await state.auth0Client!.getUser();
    setState(prevState => ({ ...prevState, popupOpen, user, isAuthenticated: true }));
  };

  const handleRedirectCallback = async () => {
    await state.auth0Client!.handleRedirectCallback();
    const user = await state.auth0Client!.getUser();
    setState(prevState => ({ ...prevState, user, isAuthenticated: true, loading: false }));
  };

  const updateMetaData = (metaData: any) => {
    const newMetaData = { ...state.user.meta_data, ...metaData };
    const userUpdated = { ...state.user, meta_data: newMetaData };
    setState(prevState => ({ ...prevState, user: { ...userUpdated } }));
  };

  const loginWithRedirect = async (params = {}) => {
    await state.auth0Client!.loginWithRedirect({
      ...params,
      appState: { returnTo: window.location.pathname + window.location.search },
    });
  };

  const configObject: ContextProps = {
    loading: state.loading,
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    popupOpen: state.popupOpen,
    loginWithPopup,
    handleRedirectCallback,
    loginWithRedirect, // Use the modified loginWithRedirect

    getTokenSilently: params => state.auth0Client!.getTokenSilently(params),
    getIdTokenClaims: params => state.auth0Client!.getIdTokenClaims(params),
    logout: params => state.auth0Client!.logout(params),
    updateMetaData,
  };

  return <Auth0Context.Provider value={configObject}>{children}</Auth0Context.Provider>;
};

export default Auth0Provider;
